import PropTypes from "prop-types";
import React from "react";
import * as Sentry from "@sentry/react";
import Auth0UserProps from "src/custom-prop-types/auth0user";
import ErrorMessage from "./ErrorPage/ErrorMessage";
import {
  isWorkspaceNotFoundUIError,
  isWorkspaceNotFoundError,
  isWorkspaceAccessDeniedError,
} from "./ErrorPage/detectErrorType";

class WorkspaceErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      error: null,
      redirecting: false,
    };
  }

  componentDidCatch(error) {
    if (
      !isWorkspaceNotFoundUIError(error) &&
      !isWorkspaceNotFoundError(error) &&
      !isWorkspaceAccessDeniedError(error)
    ) {
      throw error;
    }

    // The workspace the URL points at no longer exists (e.g. it was just
    // deleted/deprovisioned). Re-rendering here would just throw again since
    // the URL doesn't change, so redirect to the workspace-less app root
    // instead — that lets WorkspaceContextProvider fall back to the caller's
    // active/default workspace instead of crash-looping into a dead end.
    // Access-denied errors are a different case (the workspace exists, the
    // caller just can't see it) so those still surface the error page.
    if (isWorkspaceNotFoundUIError(error) || isWorkspaceNotFoundError(error)) {
      this.setState({ redirecting: true });
      // Workspace-less app root (not `workspaceRootUrl()`, which generates ""
      // for an omitted optional param, not "/") — lets
      // WorkspaceContextProvider fall back to the caller's active/default
      // workspace on reload.
      window.location = "/";
      return;
    }

    this.setState({ error });
  }

  render() {
    const { error, redirecting } = this.state;
    const { user, children } = this.props;
    if (redirecting) {
      return null;
    }
    if (error) {
      return (
        <Sentry.ErrorBoundary
          fallback={({ ...props }) => (
            <ErrorMessage
              user={user}
              showHeader={true}
              showContainer={true}
              showClientInfo={false}
              {...props}
            />
          )}
        >
          {children}
        </Sentry.ErrorBoundary>
      );
    }
    return children;
  }
}

WorkspaceErrorBoundary.propTypes = {
  user: Auth0UserProps,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
};

export default WorkspaceErrorBoundary;
